import React from "react"
import { createRoot } from "react-dom/client"
import * as Sentry from "@sentry/react"
import App from "./App.tsx"

// ==========================================================================
// FONT REGISTRY - All fonts pre-loaded so switching in index.css is instant.
// To change the active combo, only edit the --font-* variables in index.css :root
// ==========================================================================

// Combo A: Oswald / Source Serif 4 / Source Code Pro
import "@fontsource/oswald/400.css";
import "@fontsource/oswald/500.css";
import "@fontsource/oswald/600.css";
import "@fontsource/oswald/700.css";
import "@fontsource/source-serif-4/400.css";
import "@fontsource/source-serif-4/400-italic.css";
import "@fontsource/source-serif-4/600.css";
import "@fontsource/source-serif-4/700.css";
import "@fontsource/source-code-pro/400.css";
import "@fontsource/source-code-pro/700.css";

// Combo B: Space Grotesk / Sora / Space Mono
import "@fontsource/space-grotesk/400.css";
import "@fontsource/space-grotesk/500.css";
import "@fontsource/space-grotesk/600.css";
import "@fontsource/space-grotesk/700.css";
import "@fontsource/sora/400.css";
import "@fontsource/sora/600.css";
import "@fontsource/sora/700.css";
import "@fontsource/space-mono/400.css";
import "@fontsource/space-mono/700.css";

// Combo C: Plus Jakarta Sans / DM Sans / JetBrains Mono
// (Clash Display & General Sans aren't on @fontsource - these are the closest matches)
import "@fontsource/plus-jakarta-sans/400.css";
import "@fontsource/plus-jakarta-sans/500.css";
import "@fontsource/plus-jakarta-sans/600.css";
import "@fontsource/plus-jakarta-sans/700.css";
import "@fontsource/dm-sans/400.css";
import "@fontsource/dm-sans/500.css";
import "@fontsource/dm-sans/600.css";
import "@fontsource/dm-sans/700.css";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/700.css";

// Combo D: Archivo Black / Archivo / (kept for legacy references)
import "@fontsource/archivo-black";
import "@fontsource/archivo/400.css";
import "@fontsource/archivo/600.css";
import "@fontsource/archivo/700.css";

import "./index.css"

const isProd = import.meta.env.PROD
const sentryDsn = import.meta.env.VITE_SENTRY_DSN

if (isProd && sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 0.2,
    tracePropagationTargets: [/^https:\/\/api\.meatbid\.mx/],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  })
}

import { LanguageProvider, useLanguage } from "@/hooks/useLanguage"
import { ThemeProvider } from "next-themes"

const AppWithLanguage = () => {
  const { t } = useLanguage()
  return (
    <Sentry.ErrorBoundary fallback={<div>{t.common.unexpectedError}</div>}>
      <App />
    </Sentry.ErrorBoundary>
  )
}

const AppWrapper = () => {
  return (
    <ThemeProvider attribute="class" defaultTheme="light">
      <LanguageProvider>
        <AppWithLanguage />
      </LanguageProvider>
    </ThemeProvider>
  )
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <AppWrapper />
  </React.StrictMode>
)
